﻿// CUSTOM: personalised-recharge-utils.js
// Recharge API utilities: subscription fetching, bundle operations, SDK initialization.
// Depends on: personalised-recharge-session.js (sessionManager, customerIdManager, waitForRechargeSDK).

(function() {
  window.personalised = window.personalised || {};
  window.personalised.utils = window.personalised.utils || {};

  // Local copies of format helpers needed by the rechargeUtils public API
  // (also registered on window.personalised.utils by personalised-utils.js)
  function formatExternalId(id) {
    return {
      ecommerce: String(id)
    };
  }


  function formatBundleItems(items) {
    // Pull config values safely
    const collectionId = window.personalised?.config?.bundle?.RECHARGE?.COLLECTION_ID || '512297271595';
    const collectionSource = window.personalised?.config?.bundle?.RECHARGE?.COLLECTION_SOURCE || 'shopify';
    
    return items
      .filter(item => item.product_id && item.variant_id)
      .map(item => ({
        external_product_id: String(item.product_id),
        external_variant_id: String(item.variant_id),
        collection_id: collectionId,
        collection_source: collectionSource,
        quantity: item.quantity
      }));
  }

  const rechargeUtils = (function() {
    /**
     * Check if Recharge SDK is available and properly loaded
     * @returns {boolean} Whether SDK is available and ready for use
     */
    function isRechargeSDKAvailable() {
      return typeof window.recharge !== 'undefined' && !!window.recharge;
    }

    /**
     * Initialize Recharge SDK with storefront token
     * @returns {Promise<boolean>} Whether initialization succeeded
     */
    async function initRechargeSDK() {
      try {
        // SDK already initialized — skip
        if (window.rechargeInitialized) {
          console.debug('[Recharge] SDK already initialized globally');
          return true;
        }

        // SDK is initializing — wait for 'recharge:ready' with timeout
        if (window.rechargeInitializing) {
          console.debug('[Recharge] Waiting for SDK to finish initializing...');
          try {
            await new Promise((resolve, reject) => {
              const timeoutId = setTimeout(() => reject(new Error('Timed out waiting for Recharge SDK')), 5000);
              window.addEventListener('recharge:ready', () => {
                clearTimeout(timeoutId);
                resolve();
              }, { once: true });
            });
            return true;
          } catch (error) {
            console.error('[Recharge] Timeout or failure while waiting for SDK:', error);
            return false;
          }
        }

        // If SDK available but not initialized, fallback to local initialization
        if (window.recharge && window.recharge.init) {
          console.debug('[Recharge] Initializing SDK locally (fallback)');
          try {
            await window.recharge.init({
              storeIdentifier: window.Shopify?.shop || document.location.hostname,
              storefrontAccessToken: 'strfnt_4d357a0f69f38144cc1c3c548ea225c9229c04507b307b4f84cbcfc7c05eba80',
              loginRetryFn: async () => {
                try {
                  const session = await window.recharge.auth.loginShopifyAppProxy();
                  if (session) {
                    window.personalised?.sessionManager?.updateSession?.(session);
                  }
                  return session;
                } catch (error) {
                  console.error('[Recharge] Fallback loginRetryFn error:', error);
                  return null;
                }
              }
            });
            window.rechargeInitialized = true;
            window.dispatchEvent(new Event('recharge:ready'));
            return true;
          } catch (error) {
            console.error('[Recharge] Local fallback init failed:', error);
            return false;
          }
        }

        console.error('[Recharge] SDK not available');
        return false;
      } catch (err) {
        console.error('Failed to initialize Recharge SDK:', err);
        return false;
      }
    }
    


    /**
     * Fetch all subscriptions for authenticated customer
     * @returns {Promise<Array>} Array of subscription objects or empty array if error/none found
     */
    /**
     * Fetches all subscriptions for the current customer from Recharge API
     * Handles different response formats from various Recharge API versions
     * @returns {Promise<Array>} Array of subscription objects or empty array if none found
     */
    async function fetchSubscriptions() {
      try {
        if (window.personalised?.config?.debugMode) {
          console.debug('[fetchSubscriptions] Starting fetch from Recharge');
        }
        
        // Defensive guard clause for sessionManager
        if (typeof window.personalised?.sessionManager?.getSession !== 'function') {
          console.error('[fetchSubscriptions] Session manager not initialized or getSession not available');
          return [];
        }
        
        // Verify SDK is available
        if (!isRechargeSDKAvailable()) {
          console.error('Recharge SDK not available for subscription fetch');
          return [];
        }
        
        // Get session from session manager - this is the authoritative source for customer ID
        const session = await window.personalised.sessionManager.getSession();
        
        console.debug('[Subscriptions] Session state for subscription fetch:', {
          sessionExists: !!session,
          hasCustomerId: window.personalised.sessionManager.hasValidSession() && !!session?.customerId,
          customerId: window.personalised.sessionManager.getCustomerId() || 'not available',
          windowCustomerId: window.personalised.customerIdManager.get() || 'not available',
          shopifyCustomerId: Shopify?.customer?.id || 'not available'
        });
        
        if (!window.personalised.sessionManager.hasValidSession()) {
          console.error('[Subscriptions] No valid Recharge session for subscription fetch');
          return [];
        }
        
        if (!window.personalised.sessionManager.getCustomerId()) {
          console.error('[Subscriptions] Session has no customerId, cannot fetch subscriptions');
          return [];
        }
        
        // Declare response variable outside try block to maintain scope
        let response;
        
        try {
          // Ensure Recharge SDK is ready
          await window.personalised.utils.waitForRechargeSDK();
          
          // Ensure Recharge SDK subscription API is available
          if (!window.recharge?.subscription?.listSubscriptions) {
            console.error('Recharge SDK subscription.listSubscriptions API not available');
            return [];
          }

          if (window.personalised?.config?.debugMode) {
            console.debug('[fetchSubscriptions] About to call Recharge API', {
              sessionValid: !!session,
              sessionCustomerId: session.customerId,
              apiEndpoint: 'subscription.listSubscriptions',
              sdkVersion: window.recharge?.version || 'unknown'
            });
          }
          
          // Get all customer's subscriptions using the proper API method
          console.debug('[Subscriptions] Calling listSubscriptions with session:', {
            apiToken: session.apiToken ? 'present' : 'missing',
            customerId: session.customerId || 'not available'
          });
          
          response = await window.recharge.subscription.listSubscriptions(session);
        } catch (sdkError) {
          console.error('Error waiting for Recharge SDK:', sdkError);
          return [];
        }
        
        if (window.personalised?.config?.debugMode) {
          console.debug('[fetchSubscriptions] Raw response received', {
            responseType: typeof response,
            isArray: Array.isArray(response),
            hasSubscriptionsProperty: response && typeof response === 'object' && 'subscriptions' in response,
            hasNestedData: response && typeof response === 'object' && response.data && 'subscriptions' in response.data,
            responseKeys: response && typeof response === 'object' ? Object.keys(response) : []
          });
        }
        
        // Enhanced response validation to handle multiple Recharge API response formats
        let subscriptions = [];
        let formatDetected = 'unknown';
        
        // Format detection and parsing with robust fallbacks
        if (Array.isArray(response)) {
          // Format 1: Direct array response format
          subscriptions = response;
          formatDetected = 'direct-array';
          if (window.personalised?.config?.debugMode) {
            console.debug('[fetchSubscriptions] Parsed direct array response format', { 
              count: subscriptions.length,
              sample: subscriptions.length > 0 ? { id: subscriptions[0].id } : 'none' 
            });
          }
        } else if (response && Array.isArray(response.subscriptions)) {
          // Format 2: Object with subscriptions array property
          subscriptions = response.subscriptions;
          formatDetected = 'wrapped-subscriptions';
          if (window.personalised?.config?.debugMode) {
            console.debug('[fetchSubscriptions] Parsed response.subscriptions format', { 
              count: subscriptions.length,
              sample: subscriptions.length > 0 ? { id: subscriptions[0].id } : 'none'
            });
          }
        } else if (response && response.data && Array.isArray(response.data.subscriptions)) {
          // Format 3: Nested data.subscriptions format (GraphQL-style)
          subscriptions = response.data.subscriptions;
          formatDetected = 'data-subscriptions';
          if (window.personalised?.config?.debugMode) {
            console.debug('[fetchSubscriptions] Parsed response.data.subscriptions format', { 
              count: subscriptions.length,
              sample: subscriptions.length > 0 ? { id: subscriptions[0].id } : 'none'
            });
          }
        } else {
          // Unable to parse any known format - provide detailed warning
          console.warn('[fetchSubscriptions] Invalid subscriptions response format from Recharge:', {
            responseType: typeof response,
            hasData: response && typeof response.data === 'object',
            keys: response ? Object.keys(response) : [],
            payload: typeof response === 'object' ? JSON.stringify(response) : typeof response
          });
          return [];
        }
        
        // Safety check for empty array
        if (subscriptions.length === 0) {
          if (window.personalised?.config?.debugMode) {
            console.debug('[fetchSubscriptions] No subscriptions found for customer');
          }
        } else if (window.personalised?.config?.debugMode) {
          console.debug(`[fetchSubscriptions] Successfully parsed ${subscriptions.length} subscriptions using format: ${formatDetected}`);
        }
        
        return subscriptions;
      } catch (err) {
        console.error('Failed to fetch Recharge subscriptions:', err);
        return [];
      }
    }

    /**
     * Find the customer's personalised pack subscription if it exists
     * @returns {Promise<Object|null>}
     */
    /**
     * Find the customer's personalised pack subscription if it exists
     * Uses multiple matching strategies: handle and title with locale-aware spelling
     * @returns {Promise<Object|null>} The subscription object or null if not found
     */
    /**
     * Find a personalised pack subscription for the current customer
     * Uses multiple matching strategies with fallbacks to identify the correct subscription
     * @returns {Promise<Object|null>} Subscription object or null if not found
     */
    async function findPersonalisedPackSubscription() {
      try {
        // PR-1.2: Clear any stale inactive-subscription metadata before running the lookup.
        // PR-5.3: Also clear normalised subscription state fields so stale values never persist.
        // This ensures all flags reflect the current subscription reality, not a prior session.
        const _storeMgr = window.personalised?.storageManager;
        if (_storeMgr) {
          _storeMgr.clearMetadata('hasInactiveSubscription');
          _storeMgr.clearMetadata('inactiveSubscriptionId');
          _storeMgr.clearMetadata('inactiveSubscriptionStatus');
          _storeMgr.clearMetadata('subscriptionStatus');
          _storeMgr.clearMetadata('subscriptionCreatedAt');
          _storeMgr.clearMetadata('subscriptionUpdatedAt');
          _storeMgr.clearMetadata('nextChargeScheduledAt');
          _storeMgr.clearMetadata('orderIntervalFrequency');
        }

        console.debug('[Subscription Search] Starting personalised pack subscription search');
        console.debug('[Subscription Search] Customer context:', {
          customerId: window.personalised.customerIdManager.get() || 'not available',
          customerEmail: 'removed - debug only',
          hasValidSession: window.personalised.sessionManager.hasValidSession(),
          isAuthenticatedSession: window.personalised.sessionManager.isAuthenticatedSession(),
          rechargeSessionCustomerId: window.personalised.sessionManager.getCustomerId() || 'not available'
        });
        
        console.debug('[Subscription Search] About to call fetchSubscriptions()');
        
        const subscriptions = await fetchSubscriptions();
        
        // Enhanced detailed logging with explicit subscription detection
        console.log('🔍 [Subscription Debug] Full subscription data retrieved:', subscriptions);
        
        if (!subscriptions || subscriptions.length === 0) {
          console.log('⚠️ [Subscription Debug] No subscriptions found for customer');
          return null;
        }
        
        // Log detailed information about each subscription for diagnosis
        console.log(`🔄 [Subscription Debug] Found ${subscriptions.length} subscriptions:`);
        
        subscriptions.forEach((sub, i) => {
          // Extract all relevant fields that might help with matching
          const {
            id,
            status,
            sku,
            created_at,
            next_charge_scheduled_at,
            order_interval_frequency,
            external_product_id,
            external_variant_id,
            product_title
          } = sub;
          
          // Also check if these fields exist in product sub-object (Recharge API inconsistency)
          const product = sub.product || {};
          
          // Log comprehensive subscription details
          console.log(`📦 [Subscription ${i+1}/${subscriptions.length}] ID: ${id}`, {
            // Basic subscription info
            id,
            status,
            sku: sku || 'not available',
            created_at: created_at || 'not available',
            next_charge_scheduled_at: next_charge_scheduled_at || 'not available',
            frequency: order_interval_frequency || 'not available',
            
            // Direct subscription product fields
            external_product_id: external_product_id || 'not available',
            external_variant_id: external_variant_id || 'not available',
            product_title: product_title || 'not available',
            
            // Product sub-object fields
            product_id: product.id || 'not available',
            product_handle: product.handle || 'not available',
            product_title_from_object: product.title || 'not available',
            
            // Additional helpful fields
            tags: product.tags || 'not available',
            vendor: product.vendor || 'not available',
            shopify_product_id: product.shopify_product_id || external_product_id || 'not available',
            sku_from_product: product.sku || 'not available'
          });
        });
        
        if (!subscriptions || subscriptions.length === 0) {
          if (window.personalised?.config?.debugMode) {
            console.debug('[findPersonalisedPackSubscription] No subscriptions to search');
          }
          return null;
        }
        
        // Enhanced matching with detailed logging
        console.log('🧪 [Subscription Debug] Starting personalised pack detection with multiple strategies');
        
        // Track which matching strategy was used (for diagnosis)
        let matchStrategy = 'none';
        
        // Define known personalized pack identifiers
        const BUNDLE_MATCH_PATTERNS = {
          // Product IDs from config
          configuredIds: window.personalised?.config?.bundle?.KNOWN_PRODUCT_IDS || [],
          
          // Default list of known product IDs if not in config - can be expanded
          fallbackProductIds: ['9972063732011', '6948805869631'],
          
          // Common handles for personalized packs
          handles: [
            'personalised-pack', 
            'personalised-bundle' 
          ],
          
          // Title patterns for fuzzy matching
          titlePatterns: [
            'personalised pack',
            'personalised bundle'
          ],
          
          // SKU patterns that might indicate personalized products
          skuPatterns: [
            'pers-pack',
            'pers_pack',
            'personalised',
            'personalized',
            'custom-bundle'
          ]
        };
        
        console.log('🔍 [Subscription Debug] Using match patterns:', BUNDLE_MATCH_PATTERNS);
        
        // Check if we have product IDs configured for matching
        const knownProductIds = BUNDLE_MATCH_PATTERNS.configuredIds;
        const hasProductIdConfig = Array.isArray(knownProductIds) && knownProductIds.length > 0;
        
        if (!hasProductIdConfig) {
          console.log('⚠️ [Subscription Debug] No KNOWN_PRODUCT_IDS in config, will use fallback IDs and other match strategies');
        }
        
        // Track match candidates with scores for better decision making
        const matchCandidates = [];
        
        // Enhanced search using multiple patterns and fallback strategies
        subscriptions.forEach((sub, index) => {
          // Safety check for malformed subscription data
          if (!sub || typeof sub !== 'object') {
            console.log(`❌ [Subscription ${index+1}] Invalid subscription object`);
            return;
          }
          
          let matchScore = 0;
          const matchReasons = [];
          
          // Get product info from various possible locations in the subscription object
          const product = sub.product || {};
          
          // Helper function to extract product IDs from potentially nested objects
          function extractProductId(idField) {
            if (!idField) return '';
            
            // If it's a string already, return it
            if (typeof idField === 'string') return idField;
            
            // If it's an object with ecommerce property (Recharge API format)
            if (typeof idField === 'object' && idField.ecommerce) {
              return String(idField.ecommerce);
            }
            
            // Fall back to string conversion
            return String(idField);
          }
          
          // Normalize all potential ID fields for comparison
          const productIds = [
            extractProductId(product.id),
            extractProductId(product.shopify_product_id),
            extractProductId(sub.external_product_id),
            extractProductId(product.external_product_id)
          ].filter(id => id && id !== 'undefined' && id !== 'null');
          
          console.log(`🔍 [Subscription ${index+1}] Checking ID match:`, {
            subscriptionId: sub.id,
            productIds,
            knownIds: knownProductIds,
            fallbackIds: BUNDLE_MATCH_PATTERNS.fallbackProductIds
          });
          
          // Strategy 1: Match by product ID (highest priority)
          if (productIds.length > 0) {
            // Check against configured product IDs
            if (hasProductIdConfig) {
              const configMatch = productIds.some(id => knownProductIds.includes(id));
              if (configMatch) {
                matchScore += 100; // Highest confidence
                matchReasons.push('configured-product-id');
                console.log(`✅ [Subscription ${index+1}] Matched by configured product ID`);
              }
            }
            
            // Check against fallback product IDs
            const fallbackMatch = productIds.some(id => 
              BUNDLE_MATCH_PATTERNS.fallbackProductIds.includes(id));
            if (fallbackMatch) {
              matchScore += 90; // Very high confidence
              matchReasons.push('fallback-product-id');
              console.log(`✅ [Subscription ${index+1}] Matched by fallback product ID`);
            }
          }
          
          // Strategy 2: Check product handle
          const handle = String(product.handle || '').toLowerCase();
          if (handle) {
            const handleMatch = BUNDLE_MATCH_PATTERNS.handles.some(pattern => 
              handle.includes(pattern));
            if (handleMatch) {
              matchScore += 80; // High confidence
              matchReasons.push('handle');
              console.log(`✅ [Subscription ${index+1}] Matched by handle: "${handle}"`);
            }
          }
          
          // Strategy 3: Check product title (multiple sources)
          const titles = [
            String(product.title || '').toLowerCase(),
            String(sub.product_title || '').toLowerCase()
          ].filter(title => title);
          
          if (titles.length > 0) {
            const titleMatches = titles.some(title => 
              BUNDLE_MATCH_PATTERNS.titlePatterns.some(pattern => 
                title.includes(pattern)));
            
            if (titleMatches) {
              matchScore += 70; // Good confidence
              matchReasons.push('title');
              console.log(`✅ [Subscription ${index+1}] Matched by title: "${titles.join(', ')}"`);
            }
          }
          
          // Strategy 4: Check SKUs (multiple sources)
          const skus = [
            String(sub.sku || '').toLowerCase(),
            String(product.sku || '').toLowerCase()
          ].filter(sku => sku);
          
          if (skus.length > 0) {
            const skuMatches = skus.some(sku => 
              BUNDLE_MATCH_PATTERNS.skuPatterns.some(pattern => 
                sku.includes(pattern)));
            
            if (skuMatches) {
              matchScore += 60; // Medium confidence
              matchReasons.push('sku');
              console.log(`✅ [Subscription ${index+1}] Matched by SKU: "${skus.join(', ')}"`);
            }
          }
          
          // Strategy 5: Check tags if available
          if (product.tags && typeof product.tags === 'string') {
            const tags = product.tags.toLowerCase();
            if (tags.includes('personalised') || tags.includes('personalized') || 
                tags.includes('bundle') || tags.includes('subscription')) {
              matchScore += 50; // Lower confidence
              matchReasons.push('tags');
              console.log(`✅ [Subscription ${index+1}] Matched by tags: "${tags}"`);
            }
          }
          
          // Add to candidates if we have any match
          if (matchScore > 0) {
            matchCandidates.push({
              subscription: sub,
              score: matchScore,
              reasons: matchReasons,
              index
            });
          } else {
            console.log(`❌ [Subscription ${index+1}] No match found for this subscription`);
          }
        });
        
        console.log(`🧮 [Subscription Debug] Found ${matchCandidates.length} potential matches`, matchCandidates);
        
        // Select the best match based on score
        matchCandidates.sort((a, b) => {
          const sa = ((a.subscription?.status || '') + '').toUpperCase() === 'ACTIVE' ? 1 : 0;
          const sb = ((b.subscription?.status || '') + '').toUpperCase() === 'ACTIVE' ? 1 : 0;
          if (sb - sa !== 0) return sb - sa;              // ACTIVE first
          if (b.score - a.score !== 0) return b.score - a.score; // Then by match score
          // PR-5.3: Tiebreaker — most recently updated subscription wins (deterministic for
          // all-inactive cases). Fallback to created_at, then epoch 0, to avoid NaN comparisons.
          const aDate = new Date(a.subscription.updated_at || a.subscription.created_at || 0);
          const bDate = new Date(b.subscription.updated_at || b.subscription.created_at || 0);
          return bDate - aDate;
        });
        
        // Get the best match if available
        const bestMatch = matchCandidates.length > 0 ? matchCandidates[0] : null;
        const foundSubscription = bestMatch ? bestMatch.subscription : null;

        // PR-1.1 / PR-5.3: Eligibility gate — inactive subscriptions are NOT returned as the
        // subscription object. Instead, metadata flags are set so that fullInitMode() can still
        // route the customer to edit mode (PR-5.3) via the hasInactiveSubscription flag.
        // Inactive subscriptions (cancelled, paused, expired) are signalled through metadata,
        // not through this function's return value.
        if (foundSubscription) {
          const subStatus = String(foundSubscription.status || '').toLowerCase();
          if (subStatus !== 'active') {
            const dbg = window.personalised?.debug;
            if (dbg) {
              dbg.trace('subscription:inactiveFound', {
                subscriptionId: foundSubscription.id,
                status: foundSubscription.status,
                matchStrategy: (bestMatch?.reasons || []).join('+')
              });
            }
            console.log(`[Subscription Eligibility] Non-active subscription found (status: ${foundSubscription.status}) — returning null, metadata set for inactive routing`);
            const storageManager = window.personalised?.storageManager;
            if (storageManager) {
              storageManager.setMetadata('hasInactiveSubscription', true);
              storageManager.setMetadata('inactiveSubscriptionId', String(foundSubscription.id));
              storageManager.setMetadata('inactiveSubscriptionStatus', String(foundSubscription.status));
              // PR-5.3: Store normalised subscription state for later UX consumption
              storageManager.setMetadata('subscriptionStatus', String(foundSubscription.status));
              storageManager.setMetadata('subscriptionCreatedAt', foundSubscription.created_at || null);
              storageManager.setMetadata('subscriptionUpdatedAt', foundSubscription.updated_at || null);
              storageManager.setMetadata('nextChargeScheduledAt', foundSubscription.next_charge_scheduled_at || null);
              storageManager.setMetadata('orderIntervalFrequency', foundSubscription.order_interval_frequency != null ? String(foundSubscription.order_interval_frequency) : null);
            }
            return null;
          }
        }

        if (bestMatch) {
          matchStrategy = bestMatch.reasons.join('+');
          console.log(`✅ [Subscription Debug] Best match found:`, {
            subscriptionId: bestMatch.subscription.id,
            score: bestMatch.score,
            matchStrategy: matchStrategy,
            matchReasons: bestMatch.reasons
          });
        } else {
          console.log(`❌ [Subscription Debug] No personalized pack subscription found after trying all strategies`);
        }
        
        // Enhanced result logging with detailed information about the found subscription
        if (foundSubscription) {
          const dbg = window.personalised?.debug;
          if (dbg) {
            dbg.trace('subscription:activeFound', {
              subscriptionId: foundSubscription.id,
              status: foundSubscription.status,
              matchStrategy
            });
          }
          console.log(`✅ [Subscription Success] Found personalised pack subscription:`, {
            id: foundSubscription.id,
            status: foundSubscription.status,
            matchStrategy,
            productId: foundSubscription.product?.id || foundSubscription.external_product_id || 'unknown',
            title: foundSubscription.product?.title || foundSubscription.product_title || 'unknown',
            sku: foundSubscription.product?.sku || foundSubscription.sku || 'unknown',
            nextChargeDate: foundSubscription.next_charge_scheduled_at || 'unknown',
            frequency: foundSubscription.order_interval_frequency || 'unknown'
          });
          
          // PR-5.3: Store normalised subscription state for later UX consumption (active path)
          if (_storeMgr) {
            _storeMgr.setMetadata('subscriptionStatus', String(foundSubscription.status));
            _storeMgr.setMetadata('subscriptionCreatedAt', foundSubscription.created_at || null);
            _storeMgr.setMetadata('subscriptionUpdatedAt', foundSubscription.updated_at || null);
            _storeMgr.setMetadata('nextChargeScheduledAt', foundSubscription.next_charge_scheduled_at || null);
            _storeMgr.setMetadata('orderIntervalFrequency', foundSubscription.order_interval_frequency != null ? String(foundSubscription.order_interval_frequency) : null);
          }

          // Save last successful match strategy for future reference
          window.personalised._lastSuccessfulMatchStrategy = matchStrategy;
        } else {
          console.log(`❌ [Subscription Failure] No personalised pack subscription found`);
          console.log(`💡 [Subscription Tip] Consider adding your product ID to KNOWN_PRODUCT_IDS in config for more reliable detection`);
        }
        
        return foundSubscription;
      } catch (err) {
        console.error('❌ [Subscription Error] Failed to find personalised pack subscription:', err);
        return null;
      }
    }

    /**
     * Get bundle selection details for a specific subscription
     * @param {string|number} subscriptionId - Recharge subscription ID
     * @returns {Promise<Object|null>} Bundle data formatted for loadFromRecharge
     */
    async function getBundleSelectionForSubscription(subscriptionId) {
      try {
        // Verify SDK is available
        if (!isRechargeSDKAvailable()) {
          console.error('Recharge SDK not available for bundle selection fetch');
          return null;
        }
        
        // TODO: Even though this is a read operation, consider using getSession({ forceRefresh: true }) 
        // to prevent issues with stale tokens when reading sensitive customer data
        const session = await window.personalised.sessionManager.getSession();
        console.debug('[BundleUpdate] Session state for bundle fetch:', {
          sessionValid: window.personalised.sessionManager.hasValidSession(),
          sessionAuthenticated: window.personalised.sessionManager.isAuthenticatedSession(),
          hasApiToken: !!session?.apiToken,
          hasCustomerId: !!window.personalised.sessionManager.getCustomerId(),
          customerId: window.personalised.sessionManager.getCustomerId() || 'not available'
        });
        
        if (!window.personalised.sessionManager.hasValidSession()) {
          console.error('[BundleUpdate] No valid Recharge session for bundle fetch');
          return null;
        }
        
        if (!session.apiToken) {
          console.error('[BundleUpdate] No apiToken in session, cannot fetch bundle selections');
          return null;
        }

        const subId = Number(subscriptionId);
        if (isNaN(subId)) {
          console.warn(`Invalid subscription ID: ${subscriptionId}`);
          return null;
        }

        let subscription;
        let bundleSelections;

        try {
          // Ensure Recharge SDK is ready
          await window.personalised.utils.waitForRechargeSDK();
          
          if (!window.recharge?.subscription?.getSubscription || 
              !window.recharge?.bundle?.listBundleSelections) {
            console.error('Recharge SDK methods not available');
            return null;
          }

          // Get subscription first to verify it exists
          subscription = await window.recharge.subscription.getSubscription(session, subId);
          if (!subscription) {
            console.warn(`No subscription found for ID: ${subscriptionId}`);
            return null;
          }

          // Use listBundleSelections with purchase_item_ids
          console.debug('[BundleUpdate] Calling listBundleSelections for subscription:', subscription.id);
          bundleSelections = await window.recharge.bundle.listBundleSelections(session, {
            purchase_item_ids: [subscription.id],
            limit: 1
          });
        } catch (error) {
          console.error(`Error fetching bundle selection for subscription ${subId}:`, error);
          return null;
        }
        
        console.log('[Recharge] BundleSelections:', bundleSelections);

        if (!bundleSelections?.bundle_selections?.length) {
          console.warn(`[BundleUpdate] No bundle selections found for subscription: ${subscription.id}`);
          return null;
        }

        // Find the personalised pack bundle
        const bundleSelection = bundleSelections.bundle_selections.find(
          b => b.external_product_id === '9972063732011'
        );

        if (!bundleSelection) {
          console.warn(`No personalised pack bundle found for subscription: ${subscription.id}`);
          return null;
        }

        // Log critical objects for diagnostic purposes with clear identification of IDs
        const purchaseItemId = subscription.purchase_items?.[0]?.id || subscription.id;
        console.log('🔍 [DIAGNOSTIC] Bundle Fetch Objects:', {
          subscription: subscription,
          bundleSelection: bundleSelection,
          purchaseItemId: purchaseItemId,
          bundleSelectionId: bundleSelection.id, // This is critical for updateBundleSelection
          usingPurchaseItem: !!subscription.purchase_items?.[0]?.id
        });

        // Format bundle data for loadFromRecharge with enhanced metadata
        return {
          subscription: {
            id: subscription.id,
            order_interval_frequency: subscription.order_interval_frequency || '30',
            charge_interval_frequency: subscription.charge_interval_frequency || subscription.order_interval_frequency || '30',
            shipping_interval_unit_type: subscription.order_interval_unit || 'day',
            shipping_interval_frequency: subscription.order_interval_frequency || '30'
          },
          bundle: {
            id: bundleSelection.id,
            bundleRechargeId: bundleSelection.bundle_id || bundleSelection.bundle?.id || bundleSelection.id,
            external_product_id: bundleSelection.external_product_id,
            external_variant_id: bundleSelection.external_variant_id,
            purchaseItemId: purchaseItemId // Use purchase_item_id from subscription
          },
          items: bundleSelection.items.map(item => ({
            productId: item.external_product_id,
            variantId: item.external_variant_id,
            collectionId: item.collection_id || getCollectionIdFromProperties(item.properties),
            quantity: 1, // Reset to base quantity 1 – quantities from Recharge include frequency multiplier
            sellingPlanId: item.selling_plan_id || null
          })).filter(item => item.productId && item.variantId)
        };

      } catch (err) {
        console.error(`Error loading bundle for subscription ${subscriptionId}:`, err);
        return null;
      }
    }
    
    /**
     * Extract collection ID from line item properties
     * @param {Object} properties - Line item properties
     * @returns {string|null} Collection ID or null if not found
     */
    function getCollectionIdFromProperties(properties) {
      if (!properties) return null;
      const bundleKey = window.personalised?.config?.bundle?.RECHARGE_KEYS?.BUNDLE_PROPERTY || '_rc_bundle';
      return properties[bundleKey] || null;
    }
    
    /**
     * Updates the frequency of an existing subscription
     * @param {string|number} subscriptionId - The subscription ID to update
     * @param {string} frequency - The new frequency (30, 60, or 90)
     * @returns {Promise<{success: boolean, error: Error|null}>} Result object
     */
    async function updateSubscriptionFrequency(subscriptionId, frequency) {
      try {
        // Always force a fresh session for subscription updates to prevent 403 errors from stale tokens
        const session = await window.personalised.sessionManager.getSession({ forceRefresh: true });
        console.debug('[UpdateSubscriptionFrequency] Session validation (fresh):', {
          sessionValid: window.personalised.sessionManager.hasValidSession(),
          sessionAuthenticated: window.personalised.sessionManager.isAuthenticatedSession(),
          hasCustomerId: !!window.personalised.sessionManager.getCustomerId(),
          customerId: window.personalised.sessionManager.getCustomerId() || 'not available',
          subscriptionId,
          frequency
        });
        
        if (!window.personalised.sessionManager.hasValidSession()) {
          return {
            success: false,
            error: new Error('No valid Recharge session available for frequency update')
          };
        }

        // Validate frequency
        const validFrequencies = ['30', '60', '90'];
        if (!validFrequencies.includes(String(frequency))) {
          return {
            success: false,
            error: new Error(`Invalid frequency: ${frequency}. Must be one of: ${validFrequencies.join(', ')}`)
          };
        }

        // Update subscription with new frequency
        await window.recharge.subscription.updateSubscription(session, subscriptionId, {
          charge_interval_frequency: frequency,
          order_interval_frequency: frequency,
          order_interval_unit: "day"
        });

        return { success: true, error: null };
      } catch (err) {
        console.error('Subscription frequency update failed:', err);
        return { success: false, error: err };
      }
    }

    /**
     * Updates an existing bundle selection with the current state
     * Uses the correct Recharge updateBundleSelection API method for customer's bundle selection
     * @returns {Promise<{success: boolean, error: Error|null}>} Result object
     */
    async function updateBundle() {
      try {
        console.log('🔍 [Bundle Update] Validating critical metadata...');
        
        // Check for critical metadata in primary source first
        const hasMetadataInPrimary = {
          bundleSelectionId: !!window.personalised.bundle.getMetadata('bundleSelectionId'),
          subscriptionId: !!window.personalised.bundle.getMetadata('subscriptionId'),
          purchaseItemId: !!window.personalised.bundle.getMetadata('purchaseItemId')
        };
        
        // Log validation state
        console.log('[Bundle Update] Primary source metadata check:', hasMetadataInPrimary);

        // Attempt to get bundleSelectionId with fallbacks
        const bundleSelectionId = window.personalised.bundle.getMetadata('bundleSelectionId')
          || window.personalised.storageManager.getMetadata('bundleSelectionId');

        if (!hasMetadataInPrimary.bundleSelectionId && bundleSelectionId) {
          console.warn('⚠️ [Bundle Update] bundleSelectionId not found in primary bundle state, using fallback source');
        }

        if (!bundleSelectionId) {
          console.error('❌ [Bundle Update] Bundle Selection ID not found in any source. Cannot update.');
          return { 
            success: false, 
            error: new Error('Bundle Selection ID not found. Cannot update.') 
          };
        }

        // Attempt to get subscriptionId with fallbacks
        const subscriptionId = window.personalised.bundle.getMetadata('subscriptionId')
          || window.personalised.storageManager.getMetadata('subscriptionId');

        if (!hasMetadataInPrimary.subscriptionId && subscriptionId) {
          console.warn('⚠️ [Bundle Update] subscriptionId not found in primary bundle state, using fallback source');
        }
                                 
        if (!subscriptionId) {
          console.error('❌ [Bundle Update] Subscription ID not found in any source. Cannot update.');
          return { 
            success: false, 
            error: new Error('Subscription ID not found. Cannot update.') 
          };
        }
        
        // Check for Recharge SDK
        if (!isRechargeSDKAvailable()) {
          return { 
            success: false, 
            error: new Error('Recharge SDK not available') 
          };
        }
        
        // Use refreshSessionTokensOnly to reset session and tokens but preserve mode and subscription info
        // This prevents 403 errors from stale tokens after page reload or portal return
        if (window.personalised.utils?.refreshSessionTokensOnly) {
          window.personalised.utils.refreshSessionTokensOnly('update_bundle');
        }
        
        // Then get the fresh session
        const session = await window.personalised.sessionManager.getSession();
        
        console.debug('[UpdateBundle] Session validation (fresh):', {
          sessionValid: window.personalised.sessionManager.hasValidSession(),
          sessionAuthenticated: window.personalised.sessionManager.isAuthenticatedSession(),
          hasCustomerId: !!window.personalised.sessionManager.getCustomerId(),
          customerId: window.personalised.sessionManager.getCustomerId() || 'not available'
        });
        
        if (!window.personalised.sessionManager.hasValidSession()) {
          return { 
            success: false, 
            error: new Error('No valid Recharge session available for bundle update') 
          };
        }
        
        // Get current state in Recharge format (base quantities only)
        const currentState = window.personalised.bundle.toRechargeFormat();
        
        // Get originalFrequency with fallbacks
        const originalFrequency = window.personalised.bundle.getMetadata('originalFrequency')
          || window.personalised.storageManager.getMetadata('originalFrequency');
        
        if (!window.personalised.bundle.getMetadata('originalFrequency') && originalFrequency) {
          console.warn('[Fallback] originalFrequency not found in bundleState, falling back to legacy sources');
        }
                                 
        // subscriptionId is already retrieved at the start of the function
        
        // Check if frequency has changed
        if (originalFrequency && originalFrequency !== window.personalised.bundle.getFrequency()) {
          const frequencyResult = await updateSubscriptionFrequency(
            subscriptionId,
            window.personalised.bundle.getFrequency()
          );
          
          if (!frequencyResult.success) {
            return frequencyResult;
          }
        }
        
        // Get base items from state and apply frequency multiplier
        const baseItems = window.personalised.bundle.getItems();
        const multiplier = window.personalised.bundle.getFrequencyMultiplier();
        
        // Do NOT apply frequency multiplier again - it's already applied in toRechargeFormat()
        // Using currentState items directly to avoid double multiplication
        
        // Format items using utility function
        const items = window.personalised.utils.formatBundleItems(currentState.items);
        
        if (items.length === 0) {
          console.error("❌ [ERROR] No valid items to update. Aborting bundle selection update.");
          return {
            success: false,
            error: new Error("No valid items to update bundle selection with.")
          };
        }
        
        // Get purchaseItemId with fallbacks
        const purchaseItemId = window.personalised.bundle.getMetadata('purchaseItemId')
          || window.personalised.storageManager.getMetadata('purchaseItemId');

        if (!hasMetadataInPrimary.purchaseItemId && purchaseItemId) {
          console.warn('⚠️ [Bundle Update] purchaseItemId not found in primary bundle state, using fallback source');
        }

        // Log final validation state
        console.log('[Bundle Update] Final metadata validation:', {
          bundleSelectionId: !!bundleSelectionId,
          subscriptionId: !!subscriptionId,
          purchaseItemId: !!purchaseItemId
        });
                                 
        // Prepare bundle selection payload according to Recharge API requirements
        // The only required fields for updateBundleSelection are purchase_item_id and items
        const bundleSelectionPayload = {
          purchase_item_id: purchaseItemId,
          items
        };
        
        // Log detailed update information for diagnostics
        console.log('🔍 [DIAGNOSTIC] Bundle Selection Update:', {
          bundleSelectionId: bundleSelectionId,
          purchaseItemId: purchaseItemId,
          itemCount: items.length,
          payload: bundleSelectionPayload
        });
        
        // Check for missing required IDs
        if (!bundleSelectionId) {
          console.error('❌ [ERROR] Missing bundleSelectionId. Cannot update bundle selection.');
          return { 
            success: false, 
            error: new Error('Bundle Selection ID is required for update.') 
          };
        }
        
        if (!purchaseItemId) {
          console.error('❌ [ERROR] Missing purchase_item_id. Cannot update bundle selection.');
          return { 
            success: false, 
            error: new Error('Purchase item ID is required for bundle selection update.') 
          };
        }
        
        console.log("🚀 Submitting bundle selection update:", { 
          bundleSelectionId,
          purchaseItemId,
          itemCount: items.length
        });
        
        // Debug log to verify collection_id in final payload
        console.log("[DEBUG] Final Payload for updateBundleSelection:", bundleSelectionPayload);
        
        // Log quantities to verify they are correct
        console.log('📊 [Bundle Debug] Quantities being sent to Recharge:', 
          items.map(item => `${item.external_product_id}: ${item.quantity}`));
        
        // Update the bundle selection using the proper method for customer's bundle selection
        await window.recharge.bundle.updateBundleSelection(
          session,
          bundleSelectionId,
          bundleSelectionPayload,
          { commit: true }
        );
        
        // Clear the session flag after successful Recharge update
        // This allows normal Recharge data loading on subsequent page loads
        // since the local changes have been successfully synced to Recharge
        // Using storageManager to clear modified flag
        window.personalised.storageManager.clearModifiedFlag();
        
        // Return success
        return { success: true, error: null };
      } catch (err) {
        console.error('❌ [ERROR] Bundle selection update failed:', err);
        return { success: false, error: err };
      }
    }

    // Public API
    return {
      initRechargeSDK,
      fetchSubscriptions,
      findPersonalisedPackSubscription,
      getBundleSelectionForSubscription,
      isRechargeSDKAvailable,
      formatExternalId,
      formatBundleItems,
      updateBundle,
      updateSubscriptionFrequency // Expose the new function
    };
  })();
  

  window.personalised.rechargeUtils = rechargeUtils;
})();
